import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { getUserMedia } from '@public/workers/src/shared/utils/webApi/mediaDevices';

import { files } from '@/shared/utils/files';

import { VIDEO_CONFIG, VIDEO_STREAM_CONFIG } from './config';

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopped';

const downscaleStream = async (
  mediaStream: MediaStream,
  targetWidth: number,
  targetHeight: number,
  fps: number,
): Promise<MediaStream> => {
  const video = document.createElement('video');
  video.srcObject = mediaStream;
  video.muted = true;
  await video.play();

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return mediaStream;

  const outputStream = canvas.captureStream(fps);

  const draw = () => {
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth && videoHeight) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      const scale = targetHeight / videoHeight;
      const scaledWidth = videoWidth * scale;
      const scaledHeight = targetHeight;
      const offsetX = (targetWidth - scaledWidth) / 2;
      const offsetY = 0;

      ctx.drawImage(
        video,
        0,
        0,
        videoWidth,
        videoHeight,
        offsetX,
        offsetY,
        scaledWidth,
        scaledHeight,
      );
    }

    requestAnimationFrame(draw);
  };
  draw();

  mediaStream.getAudioTracks().forEach((track) => outputStream.addTrack(track));

  return outputStream;
};

export const useWebcamRecorder = (videoRef: RefObject<HTMLVideoElement>) => {
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>('idle');
  const [recordedVideoURL, setRecordedVideoURL] = useState<string | null>(null);
  const [recordedVideoBlob, setRecordedVideoBlob] = useState<Blob | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<boolean | null>(null);
  const [timer, setTimer] = useState(0);
  const [frameRate, setFrameRate] = useState<number | null>(null);
  const [resolution, setResolution] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimerInterval = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      clearTimerInterval();
    };
  }, []);

  const startCamera = useCallback(async () => {
    setRecordedVideoURL(null);
    setError(null);
    setRecordingStatus('idle');
    setTimer(0);
    clearTimerInterval();
    recordedChunksRef.current = [];

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    const rawStream = await getUserMedia(VIDEO_STREAM_CONFIG);

    if (!rawStream) {
      setError(true);
      return;
    }

    let mediaStream = rawStream;

    const videoTrack = rawStream.getVideoTracks()[0];
    const settings = videoTrack.getSettings();

    const targetSettings = {
      width: VIDEO_STREAM_CONFIG.video.width?.ideal,
      height: VIDEO_STREAM_CONFIG.video.height?.ideal,
      frameRate: VIDEO_STREAM_CONFIG.video.frameRate?.ideal,
    };

    if (
      (settings.width && settings.width > targetSettings.width) ||
      (settings.height && settings.height > targetSettings.height) ||
      (settings.frameRate && settings.frameRate > targetSettings.frameRate)
    ) {
      mediaStream = await downscaleStream(
        rawStream,
        targetSettings.width,
        targetSettings.height,
        targetSettings.frameRate,
      );
    }

    setStream(mediaStream);
    if (videoRef.current) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.muted = true;
    }

    const videoTrackSetting = mediaStream.getVideoTracks()?.[0]?.getSettings();
    if (videoTrackSetting) {
      const width = videoTrackSetting.width;
      const height = videoTrackSetting.height;
      if (width && height) {
        setResolution(`${width}x${height}`);
      }
      if (videoTrackSetting.frameRate) {
        setFrameRate(videoTrackSetting.frameRate);
      }
    }
    setIsReady(true);
  }, [videoRef, stream]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      clearTimerInterval();
    };
  }, [stream]);

  const startRecording = useCallback(() => {
    if (!stream) {
      setError(true);
      return;
    }

    setRecordingStatus('recording');
    recordedChunksRef.current = [];

    const recorder = new MediaRecorder(stream, VIDEO_CONFIG.recorderOptions);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const videoBlob = new Blob(recordedChunksRef.current, {
        type: VIDEO_CONFIG.videoContainer,
      });
      const videoUrl = files.blobToUrl(videoBlob);
      setRecordedVideoURL(videoUrl);
      setRecordedVideoBlob(videoBlob);
      clearTimerInterval();
      setRecordingStatus('stopped');
    };

    recorder.start();
    setTimer(0);
    clearTimerInterval();
    timerIntervalRef.current = setInterval(() => {
      setTimer((prevTime) => prevTime + 1);
    }, 1000);
  }, [stream]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingStatus('paused');
      clearTimerInterval();
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingStatus('recording');
      clearTimerInterval();
      timerIntervalRef.current = setInterval(() => {
        setTimer((prevTime) => prevTime + 1);
      }, 1000);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current?.state === 'recording' ||
      mediaRecorderRef.current?.state === 'paused'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // for testing if webrtc is unavailable
  //
  // const startMockCamera = useCallback(async () => {
  //   setRecordedVideoURL(null);
  //   setError(null);
  //   setRecordingStatus('idle');
  //   setTimer(0);
  //   clearTimerInterval();
  //   recordedChunksRef.current = [];

  //   if (stream) {
  //     stream.getTracks().forEach((track) => track.stop());
  //   }

  //   const canvas = document.createElement('canvas');
  //   canvas.width = 640;
  //   canvas.height = 480;
  //   const ctx = canvas.getContext('2d');

  //   let frame = 0;
  //   const draw = () => {
  //     ctx!.fillStyle = 'gray';
  //     ctx!.fillRect(0, 0, canvas.width, canvas.height);
  //     ctx!.fillStyle = 'white';
  //     ctx!.font = '30px sans-serif';
  //     ctx!.fillText(`Frame: ${frame}`, 50, 50);
  //     frame++;
  //     requestAnimationFrame(draw);
  //   };
  //   draw();

  //   const mediaStream = (canvas as any).captureStream ? canvas.captureStream(30) : null;

  //   if (!mediaStream) {
  //     setError(true);
  //     return;
  //   }

  //   setStream(mediaStream);
  //   if (videoRef.current) {
  //     videoRef.current.srcObject = mediaStream;
  //     videoRef.current.muted = true;
  //     await videoRef.current.play().catch(() => {});
  //   }
  // }, [videoRef, stream]);

  return {
    recordingStatus,
    recordedVideoURL,
    recordedVideoBlob,
    error,
    timer,
    frameRate,
    resolution,
    isReady,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    // startCamera: startMockCamera,
    startCamera,
  };
};
